import { elementAt, origemDoValor, parseJsx, raizDoBloco, type CampoDado } from './parse';

/** Recorte cirúrgico: só o trecho editado muda, o resto do arquivo fica intacto. */
function splice(src: string, start: number, end: number, text: string): string {
	return src.slice(0, start) + text + src.slice(end);
}

function escapeAttr(value: string): string {
	return value.replace(/"/g, '&quot;');
}

/** Cria, troca ou converte um atributo string. Atributo em {expressão} vira string. */
export function setAttr(src: string, loc: string, name: string, value: string): string {
	const el = elementAt(src, loc);
	if (!el) return src;

	const attr = el.attrs.find((a) => a.name === name);
	if (!attr) return splice(src, el.nameEnd, el.nameEnd, ` ${name}="${escapeAttr(value)}"`);
	if (attr.value !== null) return splice(src, attr.valueStart, attr.valueEnd, escapeAttr(value));
	return splice(src, attr.start, attr.end, `${name}="${escapeAttr(value)}"`);
}

export function removeAttr(src: string, loc: string, name: string): string {
	const el = elementAt(src, loc);
	const attr = el?.attrs.find((a) => a.name === name);
	if (!attr) return src;

	// come o espaço à esquerda para não sobrar espaço duplo
	let start = attr.start;
	while (start > 0 && /\s/.test(src[start - 1])) start--;
	return splice(src, start, attr.end, '');
}

export function removeElemento(src: string, sel: string): string | null {
	try {
		const el = elementAt(src, sel);
		if (!el || raizDoBloco(src)?.loc === el.loc) return null;

		let start = el.start;
		while (start > 0 && (src[start - 1] === ' ' || src[start - 1] === '\t')) start--;

		const quebra = /^[ \t]*\r?\n/.exec(src.slice(el.outerEnd, el.outerEnd + 40));
		const sozinho = (start === 0 || src[start - 1] === '\n') && !!quebra;
		if (!sozinho) start = el.start;

		const fora = src.slice(0, start) + src.slice(sozinho ? el.outerEnd + quebra![0].length : el.outerEnd);
		parseJsx(fora);
		return fora;
	} catch {
		return null;
	}
}

/** Troca o texto do elemento preservando a indentação em volta. */
export function setText(src: string, loc: string, text: string): string {
	const el = elementAt(src, loc);
	if (!el) return src;

	if (el.text) {
		const raw = src.slice(el.text.start, el.text.end);
		const lead = /^\s*/.exec(raw)?.[0] ?? '';
		const trail = /\s*$/.exec(raw)?.[0] ?? '';
		return splice(src, el.text.start, el.text.end, lead + text + trail);
	}
	// elemento vazio ainda tem onde escrever; auto-fechado (<img/>) não tem
	if (el.inner) return splice(src, el.inner.start, el.inner.end, text);
	return src;
}

export function setValorNaOrigem(src: string, sel: string, campo: string | null, valor: string): string {
	const origem = origemDoValor(src, sel, campo);
	if (origem) {
		const escapado = origem.jsx
			? campo === null
				? valor
				: escapeAttr(valor)
			: escapeJs(valor, src[origem.start - 1]);
		return splice(src, origem.start, origem.end, escapado);
	}

	if (campo === null) return setText(src, sel, valor);
	// atributo que ainda não existe: o setAttr cria; expressão calculada, ele respeita
	return elementAt(src, sel)?.attrs.some((a) => a.name === campo) ? src : setAttr(src, sel, campo, valor);
}

export function setCampo(src: string, campo: CampoDado, valor: string): string {
	if (campo.tipo === 'numero') {
		const n = Number(String(valor).replace(',', '.'));
		// número inválido apagaria o literal e quebraria o bloco: melhor não escrever
		if (!Number.isFinite(n)) return src;
		return splice(src, campo.start, campo.end, String(n));
	}

	if (campo.tipo === 'booleano') return splice(src, campo.start, campo.end, valor === 'true' ? 'true' : 'false');

	// `tag: null` não tem aspas para escrever dentro: vira uma string de verdade
	if (campo.tipo === 'vazio') return splice(src, campo.start, campo.end, `'${escapeJs(valor, "'")}'`);

	const aspas = src[campo.start - 1];
	return splice(src, campo.start, campo.end, escapeJs(valor, aspas));
}

/** String literal do JS: a barra e a aspas que delimita precisam sobreviver. */
function escapeJs(valor: string, aspas: string): string {
	return valor.replace(/\\/g, '\\\\').split(aspas).join(`\\${aspas}`);
}
